import {
  type Message,
  MessageDirection,
  MessageStatus,
  ConversationChannel,
  ParticipantRole,
} from 'src/api/generated';
import {
  CLIENT_MESSAGE_ID_FIELD,
  createClientMessageId,
} from './client-message-id.util';
import type {
  OptimisticWhatsAppMessageParams,
  WhatsAppClientMessageIdParams,
} from '../types/optimistic-whatsapp-message.types';

/**
 * Builds a pending snapshot without writing to a cache.
 * @param params - Message text, participants and captured local context.
 * @returns A pending message sharing the request client identity.
 */
export const createOptimisticMessage = (
  params: OptimisticWhatsAppMessageParams
): Message => {
  const { optimisticData, ...rest } = params;
  const clientMessageId =
    optimisticData?.clientMessageId ?? createClientMessageId();

  const optimisticMessage: Message = {
    id: clientMessageId,
    tenantId: '',
    providerId: '',
    direction: MessageDirection.OUTBOUND,
    status: MessageStatus.PENDING,
    metadata: {
      [CLIENT_MESSAGE_ID_FIELD]: clientMessageId,
    },
    customFields: {
      ...(rest.customFields ?? {}),
      [CLIENT_MESSAGE_ID_FIELD]: clientMessageId,
    },
    conversationId: optimisticData?.conversationId ?? '',
    funnelConversationId: optimisticData?.funnelConversationId,
    contactId: optimisticData?.contactId,
    sessionId: optimisticData?.sessionId,
    content: rest.content,
    channel: ConversationChannel.WHATSAPP,
    sender: {
      providerId: rest.fromPhoneNumber,
      address: rest.fromPhoneNumber,
      role: ParticipantRole.USER,
      displayName: rest.fromPhoneNumber,
    },
    recipient: {
      providerId: rest.toPhoneNumber,
      address: rest.toPhoneNumber,
      role: ParticipantRole.CONTACT,
      displayName: rest.toPhoneNumber,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  if (optimisticData?.filePreviewUrl && optimisticData.attachmentType) {
    optimisticMessage.attachments = [
      {
        id: `temp-attachment-${Date.now()}`,
        type: optimisticData.attachmentType,
        url: optimisticData.filePreviewUrl,
        metadata: {
          mediaType: optimisticData.mediaType,
          fileName: optimisticData.fileName,
          fileSize: optimisticData.fileSize,
        },
      },
    ];
  }

  return optimisticMessage;
};

/**
 * Captures one client identity shared by the pending snapshot and HTTP request.
 * @param params - Request fields and optional local context.
 * @returns A new request with one matching client ID in both locations.
 */
export const withClientMessageId = <
  TParams extends WhatsAppClientMessageIdParams,
>(
  params: TParams
): TParams => {
  const clientMessageId =
    params.optimisticData?.clientMessageId ?? createClientMessageId();

  return {
    ...params,
    customFields: {
      ...(params.customFields ?? {}),
      [CLIENT_MESSAGE_ID_FIELD]: clientMessageId,
    },
    optimisticData: {
      ...params.optimisticData,
      clientMessageId,
    },
  };
};
